import React, { useState, useMemo } from 'react';
import Draft from './Draft';
import { Users, Calculator, Eye, Plus, Trash2, Edit2, Save, X, RefreshCw, Gamepad2 } from 'lucide-react';
import { Footer, CookieBanner, LegalModal } from './LegalComponents';
import {
  playersDatabase,
  playStyles,
  gamesList,
  equipmentCategories,
  equipmentList,
  beanTypes,
  passiveRanges,
  playerGeneralPassives,
  playerStylePassives,
  managerPassiveTypes,
  coachPassiveTypes,
  formations
} from './data';


// Composant pour afficher l'avatar du joueur avec fond coloré selon le poste
const PlayerAvatar = ({ player, size = "w-16 h-16", className = "" }) => {
  const getRoleBackground = (role) => {
    const backgrounds = {
      'GK': '/images/GK.png',  // Orange
      'DF': '/images/DF.png',  // Vert
      'MF': '/images/MF.png',  // Bleu
      'FW': '/images/FW.png'   // Rouge
    };
    return backgrounds[role] || backgrounds['MF'];
  };

  return (
    <div 
      className={`relative ${size} rounded-full overflow-hidden ${className}`}
      style={{
        backgroundImage: `url(${getRoleBackground(player.role)})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}
    >
      <img 
        src={player.sprite} 
        alt={player.name} 
        className="w-full h-full object-cover relative z-10"
      />
    </div>
  );
};

const GAME_ORDER = [
  "Inazuma Eleven 1",
  "Inazuma Eleven 2",
  "Inazuma Eleven 3",
  "Inazuma Eleven GO",
  "Inazuma Eleven GO 2",
  "Inazuma Eleven GO Galaxy",
  "Inazuma Eleven Arès",
  "Inazuma Eleven Orion",
  "Inazuma Eleven Victory Road",
];


// ===== COMPOSANT RADAR CHART =====
const RadarChart = ({ stats, maxValue = null }) => {
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;
  const size = isMobile ? 250 : 300;
  const center = size / 2;
  const radius = size / 2 - 40;
  
  const orderedStats = [
    { label: 'Frappe', value: stats.shoot || 0 },
    { label: 'Contrôle', value: stats.control || 0 },
    { label: 'Pression', value: stats.pressure || 0 },
    { label: 'Physique', value: stats.physical || 0 },
    { label: 'Agilité', value: stats.agility || 0 },
    { label: 'Intelligence', value: stats.intelligence || 0 },
    { label: 'Technique', value: stats.technique || 0 }
  ];

  // Calcul automatique si maxValue n'est pas fourni
  const autoMaxValue = React.useMemo(() => {
  if (maxValue !== null) return maxValue;
  
  const maxStat = Math.max(...orderedStats.map(s => s.value));
  // Arrondit au multiple de 20 supérieur
  return Math.max(100, Math.ceil((maxStat + 10) / 20) * 20);
}, [orderedStats, maxValue]);

  const angleStep = (2 * Math.PI) / orderedStats.length;
  const startAngle = -Math.PI / 2;

  const getPoint = (index, value) => {
    const angle = startAngle + index * angleStep;
    const distance = (value / autoMaxValue) * radius;
    return {
      x: center + distance * Math.cos(angle),
      y: center + distance * Math.sin(angle)
    };
  };

  const getAxisEnd = (index) => {
    const angle = startAngle + index * angleStep;
    return {
      x: center + radius * Math.cos(angle),
      y: center + radius * Math.sin(angle)
    };
  };

  const getLabelPosition = (index) => {
    const angle = startAngle + index * angleStep;
    const labelDistance = radius + 25;
    return {
      x: center + labelDistance * Math.cos(angle),
      y: center + labelDistance * Math.sin(angle)
    };
  };

  const dataPoints = orderedStats.map((stat, i) => getPoint(i, stat.value));
  const pathData = dataPoints.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ') + ' Z';

  return (
    <svg width={size} height={size} className="mx-auto">
      {[0.25, 0.5, 0.75, 1].map((scale, i) => (
        <polygon
          key={i}
          points={orderedStats.map((_, index) => {
            const p = getPoint(index, autoMaxValue * scale);
            return `${p.x},${p.y}`;
          }).join(' ')}
          fill="none"
          stroke="#e5e7eb"
          strokeWidth="1"
        />
      ))}

      {orderedStats.map((_, index) => {
        const end = getAxisEnd(index);
        return (
          <line
            key={index}
            x1={center}
            y1={center}
            x2={end.x}
            y2={end.y}
            stroke="#d1d5db"
            strokeWidth="1"
          />
        );
      })}

      <path
        d={pathData}
        fill="rgba(79, 70, 229, 0.3)"
        stroke="rgb(79, 70, 229)"
        strokeWidth="2"
      />

      {dataPoints.map((point, i) => (
        <circle
          key={i}
          cx={point.x}
          cy={point.y}
          r="4"
          fill="rgb(79, 70, 229)"
        />
      ))}

      {orderedStats.map((stat, index) => {
        const pos = getLabelPosition(index);
        return (
          <text
            key={index}
            x={pos.x}
            y={pos.y}
            textAnchor="middle"
            dominantBaseline="middle"
            className="text-xs font-semibold fill-gray-700"
          >
            {stat.label}
            <tspan x={pos.x} dy="12" className="text-xs font-bold fill-indigo-600">
              {stat.value}
            </tspan>
          </text>
        );
      })}
    </svg>
  );
};

// ===== COMPOSANT CUSTOM PLAYER FORM =====
const CustomPlayerForm = ({ player = null, onSave, onCancel }) => {
  const [formData, setFormData] = useState(player || {
    id: Date.now(),
    name: '',
    role: 'FW',
    game: [],
    sprite: 'https://via.placeholder.com/80/4F46E5/FFFFFF?text=?',
    baseStats: {
      shoot: 50,
      intelligence: 50,
      control: 50,
      pressure: 50,
      agility: 50,
      physical: 50,
      technique: 50
    },
    isCustom: true
  });

  const [selectedGames, setSelectedGames] = useState(
    player?.game ? (Array.isArray(player.game) ? player.game : [player.game]) : []
  );

  const handleStatChange = (stat, value) => {
    const numValue = parseInt(value) || 0;
    setFormData(prev => ({
      ...prev,
      baseStats: {
        ...prev.baseStats,
        [stat]: Math.max(0, Math.min(999, numValue))
      }
    }));
  };

  const toggleGame = (game) => {
    setSelectedGames(prev => 
      prev.includes(game) 
        ? prev.filter(g => g !== game)
        : [...prev, game]
    );
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      alert('Le nom du joueur est requis');
      return;
    }
    if (selectedGames.length === 0) {
      alert('Sélectionnez au moins un jeu');
      return;
    }
    onSave({ ...formData, game: selectedGames });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg p-6 max-w-3xl w-full max-h-screen overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-bold">
            {player ? 'Modifier' : 'Créer'} un joueur personnalisé
          </h2>
          <button onClick={onCancel} className="text-gray-600 hover:text-gray-800">
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Nom du joueur *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                className="w-full border border-gray-300 rounded px-3 py-2"
                placeholder="Ex: Mark Evans"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Position *</label>
              <select
                value={formData.role}
                onChange={(e) => setFormData(prev => ({ ...prev, role: e.target.value }))}
                className="w-full border border-gray-300 rounded px-3 py-2"
              >
                <option value="GK">Gardien (GK)</option>
                <option value="DF">Défenseur (DF)</option>
                <option value="MF">Milieu (MF)</option>
                <option value="FW">Attaquant (FW)</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Jeux * (au moins 1)</label>
            <div className="flex flex-wrap gap-2">
              {gamesList.filter(g => g !== 'Tous les jeux').map(game => (
                <button
                  key={game}
                  type="button"
                  onClick={() => toggleGame(game)}
                  className={`px-4 py-2 rounded-lg transition-colors ${
                    selectedGames.includes(game)
                      ? 'bg-indigo-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {game}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">URL de l'image (optionnel)</label>
            <input
              type="text"
              value={formData.sprite}
              onChange={(e) => setFormData(prev => ({ ...prev, sprite: e.target.value }))}
              className="w-full border border-gray-300 rounded px-3 py-2"
              placeholder="https://exemple.com/image.png"
            />
            <p className="text-xs text-gray-500 mt-1">
              Laissez vide pour utiliser une image par défaut
            </p>
            {formData.sprite && (
              <div className="mt-2 flex items-center gap-2">
                <span className="text-xs text-gray-600">Aperçu:</span>
                <img 
                  src={formData.sprite} 
                  alt="Aperçu" 
                  className="w-12 h-12 rounded-full border-2 border-gray-300"
                  onError={(e) => e.target.src = 'https://via.placeholder.com/80/4F46E5/FFFFFF?text=?'}
                />
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium mb-3">Statistiques de base</label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {Object.entries(formData.baseStats).map(([stat, value]) => (
                <div key={stat} className="space-y-1">
                  <label className="block text-xs font-medium text-gray-600 capitalize">
                    {stat === 'shoot' ? 'Frappe' :
                     stat === 'intelligence' ? 'Intelligence' :
                     stat === 'control' ? 'Contrôle' :
                     stat === 'pressure' ? 'Pression' :
                     stat === 'agility' ? 'Agilité' :
                     stat === 'physical' ? 'Physique' :
                     stat === 'technique' ? 'Technique' : stat}
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="999"
                    value={value}
                    onChange={(e) => handleStatChange(stat, e.target.value)}
                    className="w-full border border-gray-300 rounded px-3 py-2"
                  />
                </div>
              ))}
            </div>
          </div>

          <div className="flex gap-3 pt-4 border-t">
            <button
              type="submit"
              className="flex-1 bg-indigo-600 text-white rounded-lg px-4 py-3 hover:bg-indigo-700 font-semibold flex items-center justify-center gap-2"
            >
              <Save size={20} />
              {player ? 'Mettre à jour' : 'Créer le joueur'}
            </button>
            <button
              type="button"
              onClick={onCancel}
              className="flex-1 bg-gray-300 text-gray-700 rounded-lg px-4 py-3 hover:bg-gray-400 font-semibold flex items-center justify-center gap-2"
            >
              <X size={20} />
              Annuler
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};


// ===== COMPOSANT PRINCIPAL =====
function App() {
  const [activeTab, setActiveTab] = useState('calculator');
  const [formation, setFormation] = useState('Diamant');
  const [team, setTeam] = useState({
    starters: Array(11).fill(null),
    substitutes: Array(5).fill(null),
    coach: null,
    managers: Array(3).fill(null)
  });
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [editingPlayer, setEditingPlayer] = useState(null);
  const [calculatorPlayer, setCalculatorPlayer] = useState(null);
  const [playerSelectionOpen, setPlayerSelectionOpen] = useState(false);
  const [playerSelectionPosition, setPlayerSelectionPosition] = useState(null);
  const [legalModal, setLegalModal] = useState(null);

  // Joueurs personnalisés
  const [customPlayers, setCustomPlayers] = useState(() => {
    const saved = localStorage.getItem('customPlayers');
    return saved ? JSON.parse(saved) : [];
  });
  const [showCustomPlayerForm, setShowCustomPlayerForm] = useState(false);
  const [editingCustomPlayer, setEditingCustomPlayer] = useState(null);

  React.useEffect(() => {
    localStorage.setItem('customPlayers', JSON.stringify(customPlayers));
  }, [customPlayers]);

  const allPlayers = useMemo(() => {
    return [...playersDatabase, ...customPlayers];
  }, [customPlayers]);

  
  // ===== FONCTION DE TRANSFERT DE L'ÉQUIPE DRAFTÉE =====
  const importDraftedTeam = (draftedPlayers) => {
    // Les joueurs du draft viennent directement de playersDatabase,
    // leurs champs sont déjà au bon format (id, name, role, sprite, game, baseStats)
    const convertedPlayers = draftedPlayers.map(player => ({
      ...player,
      equipments: player.equipments || [],
      beans: player.beans || [],
      passives: player.passives || [],
      playStyle: player.playStyle || 'Normal',
      isDrafted: true
    }));

    // Organiser par position
    const gk = convertedPlayers.filter(p => p.role === 'GK');
    const df = convertedPlayers.filter(p => p.role === 'DF');
    const mf = convertedPlayers.filter(p => p.role === 'MF');
    const fw = convertedPlayers.filter(p => p.role === 'FW');

    // Créer le nouveau tableau de starters (11 positions)
    const newStarters = Array(11).fill(null);
    
    // Position 0: GK
    if (gk[0]) newStarters[0] = gk[0];
    
    // Positions 1-4: DF
    df.forEach((player, i) => {
      if (i < 4) newStarters[1 + i] = player;
    });
    
    // Positions 5-8: MF
    mf.forEach((player, i) => {
      if (i < 4) newStarters[5 + i] = player;
    });
    
    // Positions 9-10: FW
    fw.forEach((player, i) => {
      if (i < 2) newStarters[9 + i] = player;
    });

    // Mettre à jour l'équipe
    setTeam(prev => ({
      ...prev,
      starters: newStarters
    }));

    // Changer vers l'onglet équipe
    setActiveTab('team');
  };

const calculateStats = (player) => {
  if (!player) return null;
  
  const base = player.baseStats || {};
  let stats = { ...base };

  // Équipements
  if (player.equipments && Array.isArray(player.equipments)) {
    player.equipments.forEach(equipment => {
      if (equipment && equipment.stats) {
        Object.entries(equipment.stats).forEach(([stat, value]) => {
          stats[stat] = (stats[stat] || 0) + value;
        });
      }
    });
  }

  // Fèves
  if (player.beans && Array.isArray(player.beans)) {
    player.beans.forEach(bean => {
      if (bean && bean.type && bean.count > 0 && base[bean.type]) {
        const bonus = Math.floor(base[bean.type] * bean.count / 100);
        stats[bean.type] = (stats[bean.type] || base[bean.type]) + bonus;
      }
    });
  }

  return stats;
};

  const evaluatePlayer = (player) => {
    if (!player || !player.passives || player.passives.length === 0) return null;
    
    let rankBCount = 0; // Nombre de passifs avec valeur B (maximale)
    let rankACount = 0; // Nombre de passifs avec valeur A (minimale)
    let totalPassives = 0;
    const passiveDetails = [];
    
    player.passives.forEach(passive => {
      if (passive && passive.name && passive.name !== "Aucun" && passiveRanges[passive.name]) {
        const range = passiveRanges[passive.name];
        const value = passive.value;
        
        totalPassives++;
        
        let rank = "";
        if (value >= range.B) {
          rank = "B";
          rankBCount++;
        } else if (value <= range.A) {
          rank = "A";
          rankACount++;
        } else {
          rank = "A+"; // Entre A et B
        }
        
        passiveDetails.push({
          name: passive.name,
          value: value,
          rank: rank,
          minValue: range.A,
          maxValue: range.B
        });
      }
    });
    
    if (totalPassives === 0) return null;
    
    // Calcul du pourcentage de passifs B
    const percentageB = (rankBCount / totalPassives) * 100;
    
    // Évaluation globale
    let rating = "";
    let color = "";
    
    if (rankBCount === totalPassives) {
      rating = "Parfait (100% B)";
      color = "text-green-600";
    } else if (percentageB >= 80) {
      rating = "Excellent";
      color = "text-green-600";
    } else if (percentageB >= 60) {
      rating = "Très Bon";
      color = "text-blue-600";
    } else if (percentageB >= 40) {
      rating = "Bon";
      color = "text-indigo-600";
    } else if (percentageB >= 20) {
      rating = "Moyen";
      color = "text-orange-600";
    } else {
      rating = "Faible";
      color = "text-red-600";
    }
    
    return { 
      rating, 
      color, 
      rankBCount, 
      rankACount, 
      totalPassives,
      percentageB: Math.round(percentageB),
      passiveDetails
    };
  };

  const allTeamMembers = useMemo(() => {
    return [
      ...team.starters.filter(p => p),
      ...team.managers.filter(m => m),
      team.coach
    ].filter(Boolean);
  }, [team]);

  const totalPassives = useMemo(() => {
    const totals = {};
    allTeamMembers.forEach(member => {
      if (member.passives) {
        member.passives.forEach(passive => {
          if (passive) {
            totals[passive.name] = (totals[passive.name] || 0) + passive.value;
          }
        });
      }
    });
    return totals;
  }, [allTeamMembers]);

  const getFieldPositions = () => {
    const form = formations[formation];
    let positions = [{ x: 10, y: 50 }];
    
    positions.push(...form.positions.def.map(p => ({ x: p[0], y: p[1], role: 'DF' })));
    positions.push(...form.positions.mid.map(p => ({ x: p[0], y: p[1], role: 'MF' })));
    positions.push(...form.positions.att.map(p => ({ x: p[0], y: p[1], role: 'FW' })));
    
    return positions;
  };

  const getPassiveTypes = (type, playStyle = null, slotIndex = 0) => {
    // Debug pour voir ce qui est passé
    console.log('🔍 getPassiveTypes - type:', type, 'playStyle:', playStyle, 'slotIndex:', slotIndex);
    
    // Vérifier si c'est un manager
    if (type === 'managers' || type === 'manager') {
      console.log('✅ Retour managerPassiveTypes:', managerPassiveTypes?.length || 0, 'items');
      return managerPassiveTypes;
    }
    
    // Vérifier si c'est un coach
    if (type === 'coach') {
      console.log('✅ Retour coachPassiveTypes:', coachPassiveTypes?.length || 0, 'items');
      return coachPassiveTypes;
    }
    
    // Pour les joueurs (starters ou substitutes)
    if (slotIndex < 3) {
      console.log('✅ Retour playerGeneralPassives (slot', slotIndex, '):', playerGeneralPassives?.length || 0, 'items');
      return playerGeneralPassives;
    } else {
      // NOUVEAU : Combiner TOUS les passifs de style (Brèche, Tension, Justice, etc.)
      const allStylePassives = [];
      Object.keys(playerStylePassives).forEach(style => {
        allStylePassives.push(...playerStylePassives[style]);
      });
      console.log('✅ Retour TOUS les passifs de style:', allStylePassives?.length || 0, 'items (tous styles combinés)');
      return allStylePassives;
    }
  };

  // ===== COMPOSANTS UI =====
  const PlayerSelector = ({ position, onSelect, onCancel }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedGame, setSelectedGame] = useState('Tous les jeux');

  const normalizeText = (s = "") =>
    String(s ?? "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .trim();

  // Renvoie le jeu de "première apparition" selon GAME_ORDER
  // (robuste même si player.game est dans un ordre bizarre)
  const getFirstAppearanceGame = (player) => {
    const games = Array.isArray(player.game)
      ? player.game
      : (player.game ? [player.game] : []);

    let best = null;
    let bestIndex = Infinity;

    for (const g of games) {
      const gNorm = normalizeText(g);

      // On compare par normalisation pour éviter "Arès/Ares" et autres variations
      const idx = GAME_ORDER.findIndex(og => normalizeText(og) === gNorm);

      if (idx !== -1 && idx < bestIndex) {
        bestIndex = idx;
        best = GAME_ORDER[idx]; // on renvoie la version canonique
      }
    }

    return best ?? (games[0] ?? null);
  };

  const searchNorm = useMemo(() => normalizeText(searchTerm), [searchTerm]);
  const selectedGameNorm = useMemo(() => normalizeText(selectedGame), [selectedGame]);

  const filteredPlayers = useMemo(() => {
    let res = allPlayers;

    // Filtre jeu = première apparition (selon l'ordre GAME_ORDER)
    if (selectedGame !== "Tous les jeux") {
      res = res.filter(p => normalizeText(getFirstAppearanceGame(p)) === selectedGameNorm);
    }

    // Recherche texte
    if (searchNorm.length > 0) {
      res = res.filter(p => normalizeText(p?.name).includes(searchNorm));
    }

    return res;
  }, [allPlayers, selectedGame, selectedGameNorm, searchNorm]);

  // Affichage limité : si aucune recherche, seulement 10 résultats
  const displayedPlayers = useMemo(() => {
    if (searchNorm.length === 0) {
    return filteredPlayers.slice(0, 24);
    }
    return filteredPlayers.slice(0, 24);
  }, [filteredPlayers, searchNorm]);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg p-6 max-w-4xl w-full max-h-screen overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">Sélectionner un joueur</h2>
          <button onClick={onCancel} className="text-gray-600 hover:text-gray-800">
            <X size={24} />
          </button>
        </div>

        <div className="space-y-4 mb-6">
          <input
            type="text"
            placeholder="Rechercher par nom..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full border border-gray-300 rounded px-3 py-2"
          />

          <div className="flex flex-wrap gap-2">
            {gamesList.map(game => (
              <button
                key={game}
                onClick={() => setSelectedGame(game)}
                className={`px-4 py-2 rounded-lg transition-colors ${
                  selectedGame === game
                    ? 'bg-indigo-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {game}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {displayedPlayers.map(player => (
            <div
              key={player.id}
              onClick={() => onSelect(player)}
              className={`border rounded-lg p-3 cursor-pointer hover:shadow-lg transition-all relative ${
                player.isCustom
                  ? 'border-green-300 bg-green-50 hover:border-green-500'
                  : 'border-gray-300 bg-white hover:border-indigo-500'
              }`}
            >
              <div className="flex flex-col items-center">
                {player.isCustom && (
                  <div className="absolute top-2 right-2 flex gap-1 z-10">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditingCustomPlayer(player);
                        setShowCustomPlayerForm(true);
                      }}
                      className="bg-blue-500 text-white p-1 rounded hover:bg-blue-600"
                      title="Modifier"
                    >
                      <Edit2 size={12} />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (window.confirm('Supprimer ce joueur personnalisé ?')) {
                          setCustomPlayers(prev => prev.filter(p => p.id !== player.id));
                        }
                      }}
                      className="bg-red-500 text-white p-1 rounded hover:bg-red-600"
                      title="Supprimer"
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                )}

                <img
                  src={player.sprite}
                  alt={player.name}
                  className="w-16 h-16 rounded-full object-cover border-2 border-indigo-200 mb-2"
                  onError={(e) => (e.target.src = 'https://via.placeholder.com/80/4F46E5/FFFFFF?text=?')}
                />
                <h3 className="font-bold text-sm text-center">{player.name}</h3>
                <span className="text-xs text-gray-600">{player.role}</span>
                <span className={`text-xs mt-1 ${player.isCustom ? 'text-green-600 font-semibold' : 'text-indigo-600'}`}>
                  {Array.isArray(player.game) ? player.game.join(', ') : player.game}
                </span>
                {player.isCustom && (
                  <span className="text-xs text-green-700 font-bold mt-1">
                    ★ Personnalisé
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>

        {filteredPlayers.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            Aucun joueur trouvé
          </div>
        )}
      </div>
    </div>
  );
};

  const PlayerEditor = ({ player, position, onSave, onCancel }) => {
    const initPassives = () => {
      const existing = player.passives || [];
      const isPlayerType = position.type === 'starters' || position.type === 'substitutes';
      const passiveTypes = isPlayerType ? playerGeneralPassives : (position.type === 'managers' ? managerPassiveTypes : coachPassiveTypes);
      
      while (existing.length < 5) {
        if (isPlayerType && existing.length >= 3) {
          const stylePassives = playerStylePassives[player.playStyle || playStyles[0]] || [];
          const passive = stylePassives[0];
          existing.push(passive ? { ...passive, value: passive.minValue } : { name: "Aucun", stat: "special", value: 0 });
        } else {
          const passive = passiveTypes[0];
          existing.push(passive ? { ...passive, value: passive.minValue } : { name: "Aucun", stat: "special", value: 0 });
        }
      }
      return existing;
    };

    const [editedPlayer, setEditedPlayer] = useState({
      ...player,
      baseStats: player.baseStats || {
        shoot: 50,
        intelligence: 50,
        control: 50,
        pressure: 50,
        agility: 50,
        physical: 50,
        technique: 50
      },
      beans: player.beans || [],
      passives: initPassives(),
      equipments: player.equipments || [],
      playStyle: player.playStyle || playStyles[0],
      type: position.type
    });

    const isPlayerType = position.type === 'starters' || position.type === 'substitutes';
    const updatePlayer = (field, value) => setEditedPlayer(prev => ({ ...prev, [field]: value }));

    const updateEquipment = (category, equipmentId) => {
      // Supprimer tous les équipements de cette catégorie
      let newEquipments = editedPlayer.equipments.filter(e => e && e.category !== category);
      
      // Ajouter le nouveau seulement si pas "Aucun"
      if (equipmentId && equipmentId !== '') {
        const equipment = equipmentList.find(e => e.id === parseInt(equipmentId));
        if (equipment) {
          newEquipments = [...newEquipments, equipment];
        }
      }
      
      updatePlayer('equipments', newEquipments);
    };

    const getEquipmentByCategory = (category) => editedPlayer.equipments.find(e => e && e.category === category);

    const addBean = () => {
      if (editedPlayer.beans.length < 3) updatePlayer('beans', [...editedPlayer.beans, { type: 'shoot', count: 0 }]);
    };

    const updateBean = (index, field, value) => {
      const beans = [...editedPlayer.beans];
      beans[index] = { ...beans[index], [field]: value };
      updatePlayer('beans', beans);
    };

    const removeBean = (index) => updatePlayer('beans', editedPlayer.beans.filter((_, i) => i !== index));

    const updatePassive = (index, passive) => {
      const passives = [...editedPlayer.passives];
      passives[index] = passive;
      updatePlayer('passives', passives);
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
        <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-screen overflow-y-auto">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold">Configurer {editedPlayer.name}</h2>
            <button onClick={onCancel} className="text-gray-600 hover:text-gray-800">
              <X size={24} />
            </button>
          </div>

          <div className="space-y-4">
            {isPlayerType && (
              <>
                <div>
                  <label className="block text-sm font-medium mb-2">Statistiques de base du joueur</label>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Frappe</label>
                      <input type="number" min="0" max="999" value={editedPlayer.baseStats?.shoot || 0} onChange={(e) => { const newStats = { ...editedPlayer.baseStats, shoot: parseInt(e.target.value) || 0 }; updatePlayer('baseStats', newStats); }} className="w-full border border-gray-300 rounded px-3 py-2" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Intelligence</label>
                      <input type="number" min="0" max="999" value={editedPlayer.baseStats?.intelligence || 0} onChange={(e) => { const newStats = { ...editedPlayer.baseStats, intelligence: parseInt(e.target.value) || 0 }; updatePlayer('baseStats', newStats); }} className="w-full border border-gray-300 rounded px-3 py-2" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Contrôle</label>
                      <input type="number" min="0" max="999" value={editedPlayer.baseStats?.control || 0} onChange={(e) => { const newStats = { ...editedPlayer.baseStats, control: parseInt(e.target.value) || 0 }; updatePlayer('baseStats', newStats); }} className="w-full border border-gray-300 rounded px-3 py-2" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Pression</label>
                      <input type="number" min="0" max="999" value={editedPlayer.baseStats?.pressure || 0} onChange={(e) => { const newStats = { ...editedPlayer.baseStats, pressure: parseInt(e.target.value) || 0 }; updatePlayer('baseStats', newStats); }} className="w-full border border-gray-300 rounded px-3 py-2" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Agilité</label>
                      <input type="number" min="0" max="999" value={editedPlayer.baseStats?.agility || 0} onChange={(e) => { const newStats = { ...editedPlayer.baseStats, agility: parseInt(e.target.value) || 0 }; updatePlayer('baseStats', newStats); }} className="w-full border border-gray-300 rounded px-3 py-2" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Physique</label>
                      <input type="number" min="0" max="999" value={editedPlayer.baseStats?.physical || 0} onChange={(e) => { const newStats = { ...editedPlayer.baseStats, physical: parseInt(e.target.value) || 0 }; updatePlayer('baseStats', newStats); }} className="w-full border border-gray-300 rounded px-3 py-2" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Technique</label>
                      <input type="number" min="0" max="999" value={editedPlayer.baseStats?.technique || 0} onChange={(e) => { const newStats = { ...editedPlayer.baseStats, technique: parseInt(e.target.value) || 0 }; updatePlayer('baseStats', newStats); }} className="w-full border border-gray-300 rounded px-3 py-2" />
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Style de jeu</label>
                  <select 
                    value={editedPlayer.playStyle} 
                    onChange={(e) => { 
                      const newStyle = e.target.value; 
                      const passives = [...editedPlayer.passives]; 
                      const stylePassives = playerStylePassives[newStyle] || []; 
                      
                      // Mettre à jour les slots 4 et 5 (index 3 et 4) avec les passifs du nouveau style
                      if (stylePassives.length > 0) { 
                        // Préserver les valeurs (A ou B) si possible
                        const oldValue3 = passives[3]?.value || stylePassives[0].minValue;
                        const oldValue4 = passives[4]?.value || stylePassives[0].minValue;
                        
                        passives[3] = { ...stylePassives[0], value: oldValue3 }; 
                        passives[4] = { ...stylePassives[0], value: oldValue4 }; 
                      } 
                      
                      updatePlayer('playStyle', newStyle); 
                      updatePlayer('passives', passives); 
                    }} 
                    className="w-full border border-gray-300 rounded px-3 py-2"
                  >
                    {playStyles.map(style => (<option key={style} value={style}>{style}</option>))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">Les passifs des slots 4 et 5 seront mis à jour automatiquement</p>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Équipements (max 4, un par catégorie)</label>
                  <div className="space-y-2">
                    {equipmentCategories.map(category => {
                      const currentEquipment = getEquipmentByCategory(category);
                      const availableEquipments = equipmentList.filter(e => e.category === category);
                      
                      return (
                        <div key={category} className="bg-gray-50 p-2 rounded">
                          <label className="block text-xs font-medium text-gray-600 mb-1">{category}</label>
                          <select 
                            key={`${category}-${currentEquipment?.id || 'none'}`}
                            value={currentEquipment?.id || ''} 
                            onChange={(e) => updateEquipment(category, e.target.value)} 
                            className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                          >
                            <option value="">Aucun</option>
                            {availableEquipments.map(eq => (
                              <option key={eq.id} value={eq.id}>
                                {eq.name}
                              </option>
                            ))}
                          </select>
                          
                          {/* Indicateur visuel */}
                          {currentEquipment && (
                            <p className="text-xs text-green-600 mt-1">
                              ✓ {currentEquipment.name}
                            </p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div>
                  <div className="flex justify-between items-center mb-2">
                    <label className="block text-sm font-medium">Fèves (max 3)</label>
                    <button onClick={addBean} disabled={editedPlayer.beans.length >= 3} className="text-blue-600 hover:text-blue-800 disabled:text-gray-400 text-sm flex items-center gap-1"><Plus size={16} />Ajouter</button>
                  </div>
                  <div className="space-y-2">
                    {editedPlayer.beans.map((bean, i) => (<div key={i} className="flex gap-2 bg-gray-50 p-2 rounded"><select value={bean.type} onChange={(e) => updateBean(i, 'type', e.target.value)} className="flex-1 border border-gray-300 rounded px-2 py-1 text-sm">{beanTypes.map(bt => (<option key={bt.key} value={bt.key}>{bt.label}</option>))}</select><input type="number" min="0" max="99" value={bean.count} onChange={(e) => updateBean(i, 'count', parseInt(e.target.value) || 0)} className="w-20 border border-gray-300 rounded px-2 py-1" placeholder="Qté" /><button onClick={() => removeBean(i)} className="text-red-600 hover:text-red-800"><Trash2 size={16} /></button></div>))}
                    {editedPlayer.beans.length === 0 && (<p className="text-gray-500 text-sm italic">Aucune fève ajoutée</p>)}
                  </div>
                </div>
              </>
            )}

            <div>
              <label className="block text-sm font-medium mb-2">Passifs (5 slots){isPlayerType && (<span className="text-xs text-gray-500 block">Slots 1-3: Généraux | Slots 4-5: Tous styles</span>)}</label>
              <div className="space-y-2">
                {editedPlayer.passives.map((passive, i) => { const availablePassives = isPlayerType ? getPassiveTypes(position.type, editedPlayer.playStyle, i) : getPassiveTypes(position.type); 
console.log('📋 Passif slot', i, '- isPlayerType:', isPlayerType, '- position.type:', position.type, '- availablePassives:', availablePassives?.length || 0, 'items', availablePassives?.slice(0, 3)?.map(p => p.name)); const slotLabel = isPlayerType ? (i < 3 ? `Slot ${i + 1} (Général)` : `Slot ${i + 1} (Style)`) : `Slot ${i + 1}`; const selectedPassiveData = availablePassives.find(p => p.name === passive.name);
const currentRange = selectedPassiveData ? { A: selectedPassiveData.minValue, B: selectedPassiveData.maxValue } : passiveRanges[passive.name];
console.log('💰 Valeurs passif "' + passive.name + '" - A:', currentRange?.A, 'B:', currentRange?.B, '(source:', selectedPassiveData ? 'availablePassives' : 'passiveRanges', ')'); return (<div key={i} className="space-y-2 bg-gray-50 p-3 rounded"><div className="flex gap-2 items-center"><span className="text-xs text-gray-500 w-32">{slotLabel}</span><select value={passive.name} onChange={(e) => { const selectedPassive = availablePassives.find(p => p.name === e.target.value); updatePassive(i, { ...selectedPassive, value: selectedPassive.minValue }); }} className="flex-1 border border-gray-300 rounded px-2 py-1 text-sm">{availablePassives.map(pt => (<option key={pt.name} value={pt.name}>{pt.name}</option>))}</select></div>{currentRange && (<div className="flex gap-2 items-center"><span className="text-xs text-gray-600 w-32">Valeur:</span><div className="flex-1 flex gap-2"><button onClick={() => updatePassive(i, { ...passive, value: currentRange.A })} className={`flex-1 px-3 py-1 rounded text-sm font-semibold transition-colors ${passive.value === currentRange.A ? 'bg-orange-500 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}>A ({currentRange.A})</button><button onClick={() => updatePassive(i, { ...passive, value: currentRange.B })} className={`flex-1 px-3 py-1 rounded text-sm font-semibold transition-colors ${passive.value === currentRange.B ? 'bg-green-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}>B ({currentRange.B})</button></div></div>)}</div>); })}
              </div>
            </div>

            <div className="flex gap-2 pt-4">
              <button onClick={() => onSave(editedPlayer, position)} className="flex-1 bg-blue-600 text-white rounded px-4 py-2 hover:bg-blue-700 flex items-center justify-center gap-2"><Save size={20} />Sauvegarder</button>
              <button onClick={onCancel} className="flex-1 bg-gray-300 text-gray-700 rounded px-4 py-2 hover:bg-gray-400 flex items-center justify-center gap-2"><X size={20} />Annuler</button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const PlayerCard = ({ player, position, onEdit, onRemove }) => {
    if (!player) return null;

    const stats = calculateStats(player);
    const evaluation = evaluatePlayer(player);

    return (
      <div className="bg-white border-2 border-gray-200 rounded-lg p-4 hover:border-indigo-400 transition-all">
        <div className="flex items-start gap-3">
          <PlayerAvatar 
            player={player}
            className="border-2 border-indigo-300 shadow-lg"
          />
          <div className="flex-1">
            <h3 className="font-bold text-lg">{player.name}</h3>
            <span className="text-xs text-indigo-600 mt-1">
  {Array.isArray(player.game) ? player.game.join(', ') : player.game}
</span>
            {player.playStyle && (
              <p className="text-xs text-indigo-600 mt-1">Style: {player.playStyle}</p>
            )}
            {evaluation && (
              <p className={`text-xs font-semibold mt-1 ${evaluation.color}`}>
                {evaluation.rating} ({evaluation.rankBCount}B/{evaluation.totalPassives})
              </p>
            )}
          </div>
          <div className="flex flex-col gap-1">
            <button
              onClick={() => onEdit(player, position)}
              className="text-blue-600 hover:text-blue-800 p-1"
            >
              <Edit2 size={18} />
            </button>
            <button
              onClick={() => onRemove(position)}
              className="text-red-600 hover:text-red-800 p-1"
            >
              <Trash2 size={18} />
            </button>
          </div>
        </div>

        {stats && (
          <div className="mt-3 grid grid-cols-4 gap-2 text-xs">
            <div className="bg-red-50 p-2 rounded">
              <div className="font-semibold text-red-700">Frappe</div>
              <div className="text-lg font-bold text-red-900">{stats.shoot}</div>
            </div>
            <div className="bg-blue-50 p-2 rounded">
              <div className="font-semibold text-blue-700">Intel.</div>
              <div className="text-lg font-bold text-blue-900">{stats.intelligence}</div>
            </div>
            <div className="bg-green-50 p-2 rounded">
              <div className="font-semibold text-green-700">Contrôle</div>
              <div className="text-lg font-bold text-green-900">{stats.control}</div>
            </div>
            <div className="bg-purple-50 p-2 rounded">
              <div className="font-semibold text-purple-700">Tech.</div>
              <div className="text-lg font-bold text-purple-900">{stats.technique}</div>
            </div>
          </div>
        )}

        {player.equipments && player.equipments.length > 0 && (
          <div className="mt-2">
            <p className="text-xs font-semibold text-gray-600 mb-1">Équipements:</p>
            <div className="flex flex-wrap gap-1">
              {player.equipments.map((eq, i) => (
                <span key={i} className="text-xs bg-indigo-100 text-indigo-700 px-2 py-1 rounded">
                  {eq.name}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  const handleSelectPlayer = (player) => {
    if (!playerSelectionPosition) return;

    const { type, index } = playerSelectionPosition;
    const newTeam = { ...team };

    const configuredPlayer = {
      ...player,
      beans: [],
      passives: [],
      equipments: [],
      playStyle: playStyles[0]
    };

    if (type === 'starters' || type === 'substitutes') {
      newTeam[type][index] = configuredPlayer;
    } else if (type === 'coach') {
      newTeam.coach = configuredPlayer;
    } else if (type === 'managers') {
      newTeam.managers[index] = configuredPlayer;
    }

    setTeam(newTeam);
    setPlayerSelectionOpen(false);
    setPlayerSelectionPosition(null);
    
    // Ouvrir directement l'éditeur
    setEditingPlayer({ player: configuredPlayer, position: playerSelectionPosition });
  };

  const handleSavePlayer = (editedPlayer, position) => {
    const newTeam = { ...team };
    const { type, index } = position;

    if (type === 'starters' || type === 'substitutes') {
      newTeam[type][index] = editedPlayer;
    } else if (type === 'coach') {
      newTeam.coach = editedPlayer;
    } else if (type === 'managers') {
      newTeam.managers[index] = editedPlayer;
    }

    setTeam(newTeam);
    setEditingPlayer(null);
  };

  const handleRemovePlayer = (position) => {
    const newTeam = { ...team };
    const { type, index } = position;

    if (type === 'starters' || type === 'substitutes') {
      newTeam[type][index] = null;
    } else if (type === 'coach') {
      newTeam.coach = null;
    } else if (type === 'managers') {
      newTeam.managers[index] = null;
    }

    setTeam(newTeam);
  };
// Handlers pour les joueurs personnalisés
  const handleSaveCustomPlayer = (playerData) => {
    if (editingCustomPlayer) {
      // Mise à jour d'un joueur existant
      setCustomPlayers(prev => prev.map(p => p.id === playerData.id ? playerData : p));
    } else {
      // Création d'un nouveau joueur
      setCustomPlayers(prev => [...prev, playerData]);
    }
    setShowCustomPlayerForm(false);
    setEditingCustomPlayer(null);
  };

  // ===== RENDU PRINCIPAL =====
  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 flex flex-col">
      <div className="flex-1 container mx-auto px-2 sm:px-4 py-4 sm:py-8">
        <header className="text-center mb-4 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent mb-2">
            Team Builder Inazuma Eleven Victory Road
          </h1>
          <p className="text-sm sm:text-base text-gray-600">Optimisez votre équipe, Vérifiez les stats, Créez une équipe au hasard.</p>
          <p className="text-xs sm:text-sm text-yellow-600">Des données sont en cours d'ajouts, il est donc possible que vous ne trouviez pas ce que vous cherchiez</p>
        </header>

        {/* Barre de navigation - grille 2x2 sur mobile, ligne sur desktop */}
        <div className="grid grid-cols-2 sm:flex sm:flex-wrap sm:justify-center mb-4 sm:mb-6 gap-2 sm:gap-3">
          <button
            onClick={() => setActiveTab('team')}
            className={`px-3 sm:px-6 py-2 sm:py-3 rounded-lg font-semibold transition-all flex items-center justify-center gap-1 sm:gap-2 text-sm sm:text-base ${
              activeTab === 'team'
                ? 'bg-indigo-600 text-white shadow-lg'
                : 'bg-white text-gray-700 hover:bg-gray-100 shadow'
            }`}
          >
            <Users size={16} className="sm:w-5 sm:h-5" />
            Équipe
          </button>
          <button
            onClick={() => setActiveTab('calculator')}
            className={`px-3 sm:px-6 py-2 sm:py-3 rounded-lg font-semibold transition-all flex items-center justify-center gap-1 sm:gap-2 text-sm sm:text-base ${
              activeTab === 'calculator'
                ? 'bg-indigo-600 text-white shadow-lg'
                : 'bg-white text-gray-700 hover:bg-gray-100 shadow'
            }`}
          >
            <Calculator size={16} className="sm:w-5 sm:h-5" />
            Calculateur
          </button>
          <button
            onClick={() => setActiveTab('visualization')}
            className={`px-3 sm:px-6 py-2 sm:py-3 rounded-lg font-semibold transition-all flex items-center justify-center gap-1 sm:gap-2 text-sm sm:text-base ${
              activeTab === 'visualization'
                ? 'bg-indigo-600 text-white shadow-lg'
                : 'bg-white text-gray-700 hover:bg-gray-100 shadow'
            }`}
          >
            <Eye size={16} className="sm:w-5 sm:h-5" />
            Visualisation
          </button>
          {/* Bouton Draft toujours mis en valeur */}
          <button
            onClick={() => setActiveTab('draft')}
            className={`px-3 sm:px-6 py-2 sm:py-3 rounded-lg font-semibold transition-all flex items-center justify-center gap-1 sm:gap-2 text-sm sm:text-base shadow-lg ${
              activeTab === 'draft'
                ? 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white ring-2 ring-purple-400 ring-offset-1'
                : 'bg-gradient-to-r from-purple-500 to-indigo-500 text-white hover:from-purple-600 hover:to-indigo-600'
            }`}
          >
            <Gamepad2 size={16} className="sm:w-5 sm:h-5" />
            ⚡ Draft
          </button>
        </div>

    

        {/* ===== ONGLET ÉQUIPE ===== */}
        {activeTab === 'team' && (
          <div className="space-y-4 sm:space-y-6">
            <div className="bg-white rounded-lg shadow-lg p-3 sm:p-6">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-2">
                <h2 className="text-xl sm:text-2xl font-bold">Formation</h2>
                <select
                  value={formation}
                  onChange={(e) => setFormation(e.target.value)}
                  className="border border-gray-300 rounded px-3 sm:px-4 py-2 text-sm sm:text-base w-full sm:w-auto"
                >
                  {Object.keys(formations).map(f => (
                    <option key={f} value={f}>{f}</option>
                  ))}
                </select>
              </div>

              <div className="relative bg-gradient-to-b from-green-600 to-green-700 rounded-lg h-64 sm:h-80 md:h-96 mb-4 sm:mb-6">
                {getFieldPositions().map((pos, i) => {
                  const player = team.starters[i];
                  return (
                    <div
                      key={i}
                      style={{ left: `${pos.x}%`, top: `${pos.y}%` }}
                      className="absolute transform -translate-x-1/2 -translate-y-1/2"
                    >
                      {player ? (
                        <div 
                          className="cursor-pointer hover:scale-110 transition-transform border-2 border-indigo-500 rounded-full"
                          onClick={() => setEditingPlayer({ player, position: { type: 'starters', index: i } })}
                        >
                          <PlayerAvatar player={player} size="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12" />
                        </div>
                      ) : (
                        <button
                          onClick={() => {
                            setPlayerSelectionPosition({ type: 'starters', index: i });
                            setPlayerSelectionOpen(true);
                          }}
                          className="bg-white bg-opacity-50 rounded-full w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 flex items-center justify-center border-2 border-dashed border-white hover:bg-opacity-75 transition-all"
                        >
                          <Plus className="text-white w-4 h-4 sm:w-5 sm:h-5" />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                {team.starters.filter(p => p).map((player, i) => (
                  <PlayerCard
                    key={i}
                    player={player}
                    position={{ type: 'starters', index: team.starters.indexOf(player) }}
                    onEdit={(p, pos) => setEditingPlayer({ player: p, position: pos })}
                    onRemove={handleRemovePlayer}
                  />
                ))}
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-lg p-3 sm:p-6">
              <h2 className="text-xl sm:text-2xl font-bold mb-4">Remplaçants</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                {team.substitutes.map((player, i) => (
                  <div key={i}>
                    {player ? (
                      <PlayerCard
                        player={player}
                        position={{ type: 'substitutes', index: i }}
                        onEdit={(p, pos) => setEditingPlayer({ player: p, position: pos })}
                        onRemove={handleRemovePlayer}
                      />
                    ) : (
                      <button
                        onClick={() => {
                          setPlayerSelectionPosition({ type: 'substitutes', index: i });
                          setPlayerSelectionOpen(true);
                        }}
                        className="w-full h-32 border-2 border-dashed border-gray-300 rounded-lg hover:border-indigo-500 hover:bg-indigo-50 transition-all flex items-center justify-center"
                      >
                        <Plus className="text-gray-400" size={32} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
              <div className="bg-white rounded-lg shadow-lg p-3 sm:p-6">
                <h2 className="text-xl sm:text-2xl font-bold mb-4">Entraîneur</h2>
                {team.coach ? (
                  <PlayerCard
                    player={team.coach}
                    position={{ type: 'coach' }}
                    onEdit={(p, pos) => setEditingPlayer({ player: p, position: pos })}
                    onRemove={handleRemovePlayer}
                  />
                ) : (
                  <button
                    onClick={() => {
                      setPlayerSelectionPosition({ type: 'coach' });
                      setPlayerSelectionOpen(true);
                    }}
                    className="w-full h-32 border-2 border-dashed border-gray-300 rounded-lg hover:border-indigo-500 hover:bg-indigo-50 transition-all flex items-center justify-center"
                  >
                    <Plus className="text-gray-400" size={32} />
                  </button>
                )}
              </div>

              <div className="bg-white rounded-lg shadow-lg p-3 sm:p-6">
                <h2 className="text-xl sm:text-2xl font-bold mb-4">Managers</h2>
                <div className="space-y-4">
                  {team.managers.map((manager, i) => (
                    <div key={i}>
                      {manager ? (
                        <PlayerCard
                          player={manager}
                          position={{ type: 'managers', index: i }}
                          onEdit={(p, pos) => setEditingPlayer({ player: p, position: pos })}
                          onRemove={handleRemovePlayer}
                        />
                      ) : (
                        <button
                          onClick={() => {
                            setPlayerSelectionPosition({ type: 'managers', index: i });
                            setPlayerSelectionOpen(true);
                          }}
                          className="w-full h-24 border-2 border-dashed border-gray-300 rounded-lg hover:border-indigo-500 hover:bg-indigo-50 transition-all flex items-center justify-center"
                        >
                          <Plus className="text-gray-400" size={24} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ===== ONGLET CALCULATEUR ===== */}
        {activeTab === 'calculator' && (
          <div className="bg-white rounded-lg shadow-lg p-3 sm:p-6">
            <h2 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6">Calculateur de Statistiques</h2>
            
            {!calculatorPlayer ? (
              <div className="text-center py-12">
                <p className="text-gray-600 mb-4">Sélectionnez un joueur pour calculer ses statistiques</p>
                <button
                  onClick={() => {
                    setPlayerSelectionPosition({ type: 'calculator' });
                    setPlayerSelectionOpen(true);
                  }}
                  className="bg-indigo-600 text-white px-6 py-3 rounded-lg hover:bg-indigo-700 flex items-center gap-2 mx-auto"
                >
                  <Plus size={20} />
                  Sélectionner un joueur
                </button>
              </div>
            ) : (
              <div className="space-y-4 sm:space-y-6">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-0">
                  <div className="flex items-center gap-3 sm:gap-4">
                    <img 
                      src={calculatorPlayer.sprite} 
                      alt={calculatorPlayer.name} 
                      className="w-16 h-16 sm:w-20 sm:h-20 rounded-full border-4 border-indigo-300"
                    />
                    <div>
                      <h3 className="text-lg sm:text-2xl font-bold">{calculatorPlayer.name}</h3>
                      <p className="text-sm sm:text-base text-gray-600">{calculatorPlayer.role} • {calculatorPlayer.game}</p>
                      {calculatorPlayer.playStyle && (
                        <p className="text-xs text-indigo-600 mt-1">Style: {calculatorPlayer.playStyle}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2 w-full sm:w-auto">
                    <button
                      onClick={() => setEditingPlayer({ player: calculatorPlayer, position: { type: 'calculator' } })}
                      className="bg-blue-600 text-white px-3 sm:px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2 text-sm sm:text-base flex-1 sm:flex-initial justify-center"
                    >
                      <Edit2 size={16} className="sm:w-[18px] sm:h-[18px]" />
                      <span className="hidden sm:inline">Configurer</span>
                      <span className="sm:hidden">Config</span>
                    </button>
                    <button
                      onClick={() => setCalculatorPlayer(null)}
                      className="bg-red-600 text-white px-3 sm:px-4 py-2 rounded-lg hover:bg-red-700 flex items-center gap-2 text-sm sm:text-base flex-1 sm:flex-initial justify-center"
                    >
                      <RefreshCw size={16} className="sm:w-[18px] sm:h-[18px]" />
                      <span className="hidden sm:inline">Réinitialiser</span>
                      <span className="sm:hidden">Reset</span>
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                  <div>
                    <h3 className="text-lg sm:text-xl font-bold mb-4">Statistiques de Base (Éditables)</h3>
                    <div className="space-y-3">
                      {Object.entries(calculatorPlayer.baseStats || {}).map(([stat, value]) => {
                        const statLabels = {
                          shoot: 'Frappe',
                          intelligence: 'Intelligence',
                          control: 'Contrôle',
                          pressure: 'Pression',
                          agility: 'Agilité',
                          physical: 'Physique',
                          technique: 'Technique'
                        };
                        
                        return (
                          <div key={stat} className="flex items-center gap-2 sm:gap-3 bg-gray-50 p-2 sm:p-3 rounded">
                            <label className="font-semibold w-20 sm:w-32 text-sm sm:text-base">{statLabels[stat]}</label>
                            <input
                              type="number"
                              min="0"
                              max="999"
                              value={value}
                              onChange={(e) => {
                                const newValue = parseInt(e.target.value) || 0;
                                setCalculatorPlayer(prev => ({
                                  ...prev,
                                  baseStats: {
                                    ...prev.baseStats,
                                    [stat]: Math.max(0, Math.min(999, newValue))
                                  }
                                }));
                              }}
                              className="flex-1 border border-gray-300 rounded px-2 sm:px-3 py-1 sm:py-2 text-base sm:text-lg font-bold text-indigo-600"
                            />
                          </div>
                        );
                      })}
                    </div>
                    
                    {calculateStats(calculatorPlayer) && (
                      <div className="mt-4 sm:mt-6">
                        <h3 className="text-lg sm:text-xl font-bold mb-4">Stats Finales (avec bonus)</h3>
                        <div className="space-y-2">
                          {Object.entries(calculateStats(calculatorPlayer)).map(([stat, value]) => {
                            const baseValue = calculatorPlayer.baseStats[stat];
                            const bonus = value - baseValue;
                            const statLabels = {
                              shoot: 'Frappe',
                              intelligence: 'Intelligence',
                              control: 'Contrôle',
                              pressure: 'Pression',
                              agility: 'Agilité',
                              physical: 'Physique',
                              technique: 'Technique'
                            };
                            
                            return (
                              <div key={stat} className="flex justify-between items-center bg-indigo-50 p-2 rounded">
                                <span className="font-semibold text-sm">{statLabels[stat]}</span>
                                <div className="flex items-center gap-2">
                                  {bonus > 0 && (
                                    <span className="text-green-600 font-semibold text-sm">+{bonus}</span>
                                  )}
                                  <span className="text-lg font-bold text-indigo-600">{value}</span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                  <div>
                    <h3 className="text-lg sm:text-xl font-bold mb-4">Graphique Radar</h3>
                    {calculateStats(calculatorPlayer) && (
                      <RadarChart stats={calculateStats(calculatorPlayer)} />
                    )}
                  </div>
                </div>

                                {/* Section Équipements - VERSION CORRIGÉE */}
                <div className="bg-white rounded-lg border-2 border-gray-200 p-3 sm:p-6">
                  <h3 className="text-lg sm:text-xl font-bold mb-4">Équipements (max 4, un par catégorie)</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                    {equipmentCategories.map(category => {
                      const currentEquipment = calculatorPlayer.equipments?.find(e => e && e.category === category);
                      const availableEquipments = equipmentList.filter(e => e.category === category);
                      
                      return (
                        <div key={category} className="bg-gray-50 p-3 rounded">
                          <label className="block text-sm font-medium text-gray-700 mb-2">{category}</label>
                          <select
                            key={`${category}-${currentEquipment?.id || 'none'}`}
                            value={currentEquipment?.id || ''}
                            onChange={(e) => {
                              const selectedId = e.target.value;
                              
                              console.log('🔧 Changement équipement:', {
                                categorie: category,
                                ancien: currentEquipment?.name || 'Aucun',
                                nouveau: selectedId ? availableEquipments.find(eq => eq.id === parseInt(selectedId))?.name : 'Aucun',
                                equipementsAvant: calculatorPlayer.equipments?.length || 0
                              });
                              
                              // Supprimer TOUS les équipements de cette catégorie
                              let newEquipments = (calculatorPlayer.equipments || []).filter(
                                eq => eq && eq.category !== category
                              );
                              
                              // Ajouter le nouveau seulement si pas "Aucun"
                              if (selectedId && selectedId !== '') {
                                const equipment = equipmentList.find(eq => eq.id === parseInt(selectedId));
                                if (equipment) {
                                  newEquipments = [...newEquipments, equipment];
                                }
                              }
                              
                              console.log('🔧 Équipements après:', newEquipments.length);
                              
                              // Forcer la mise à jour
                              setCalculatorPlayer(prev => ({
                                ...prev,
                                equipments: newEquipments
                              }));
                            }}
                            className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                          >
                            <option value="">Aucun</option>
                            {availableEquipments.map(eq => (
                              <option key={eq.id} value={eq.id}>
                                {eq.name}
                              </option>
                            ))}
                          </select>
                          
                          {/* Indicateur visuel de l'équipement sélectionné */}
                          {currentEquipment && (
                            <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded">
                              <p className="text-xs font-semibold text-green-700">
                                ✓ {currentEquipment.name}
                              </p>
                              <p className="text-xs text-green-600">
                                {Object.entries(currentEquipment.stats).map(([stat, value]) => {
                                  const statLabels = {
                                    shoot: 'Frappe',
                                    control: 'Contrôle',
                                    pressure: 'Pression',
                                    physical: 'Physique',
                                    agility: 'Agilité',
                                    intelligence: 'Intelligence',
                                    technique: 'Technique'
                                  };
                                  return `${statLabels[stat] || stat} +${value}`;
                                }).join(', ')}
                              </p>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  
                  {/* Liste complète des équipements pour debug */}
                  {calculatorPlayer.equipments && calculatorPlayer.equipments.length > 0 && (
                    <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded">
                      <p className="text-xs font-semibold text-blue-800 mb-1">
                        📦 Total équipements : {calculatorPlayer.equipments.length}/4
                      </p>
                      <div className="text-xs text-blue-700 space-y-1">
                        {calculatorPlayer.equipments.map((eq, i) => (
                          <div key={i}>
                            • <span className="font-semibold">{eq.name}</span> ({eq.category})
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                  </div>
                )}
              </div>
            )}
          </div>

        {/* ===== ONGLET DRAFT ===== */}
        {activeTab === 'draft' && (
          <div className="space-y-6">
            <Draft onImportTeam={importDraftedTeam} />
          </div>
        )}

        {/* ===== ONGLET VISUALISATION ===== */}
        {activeTab === 'visualization' && (
          <div className="space-y-4 sm:space-y-6">
            <div className="bg-white rounded-lg shadow-lg p-3 sm:p-6">
              <h2 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6">Passifs Totaux de l'Équipe</h2>
              {Object.keys(totalPassives).length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                  {Object.entries(totalPassives)
                    .sort(([, valueA], [, valueB]) => valueB - valueA) // Tri par valeur décroissante
                    .map(([name, value]) => (
                    <div key={name} className="bg-gradient-to-br from-indigo-50 to-purple-50 p-4 rounded-lg border border-indigo-200">
                      <div className="flex justify-between items-center">
                        <span className="font-semibold text-gray-700">{name}</span>
                        <span className="text-2xl font-bold text-indigo-600">+{value}%</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-gray-500 py-8">Aucun passif actif dans l'équipe</p>
              )}
            </div>

            <div className="bg-white rounded-lg shadow-lg p-3 sm:p-6">
              <h2 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6">Statistiques de l'Équipe</h2>
              {allTeamMembers.length > 0 ? (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                  {allTeamMembers.map((member, i) => {
                    const stats = calculateStats(member);
                    const evaluation = evaluatePlayer(member);
                    return (
                      <div key={i} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex items-center gap-3 mb-4">
                          <img 
                            src={member.sprite} 
                            alt={member.name} 
                            className="w-12 h-12 rounded-full border-2 border-indigo-300"
                          />
                          <div className="flex-1">
                            <h3 className="font-bold">{member.name}</h3>
                            <p className="text-sm text-gray-600">{member.role}</p>
                            {evaluation && (
                              <p className={`text-xs font-semibold ${evaluation.color}`}>
                                {evaluation.rating} ({evaluation.rankBCount}B/{evaluation.totalPassives})
                              </p>
                            )}
                          </div>
                        </div>
                        {stats && <RadarChart stats={stats} autoMaxValue={120} />}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-center text-gray-500 py-8">Aucun membre dans l'équipe</p>
              )}
            </div>
          </div>
        )}

      {/* Modales */}
      {playerSelectionOpen && (
        <PlayerSelector
          position={playerSelectionPosition}
          onSelect={handleSelectPlayer}
          onCancel={() => {
            setPlayerSelectionOpen(false);
            setPlayerSelectionPosition(null);
          }}
        />
      )}

      {editingPlayer && (
        
        <PlayerEditor
          player={editingPlayer.player}
          position={editingPlayer.position}
          onSave={(player, position) => {
            if (position.type === 'calculator') {
              setCalculatorPlayer(player);
            } else {
              handleSavePlayer(player, position);
            }
            setEditingPlayer(null);
          }}
          onCancel={() => setEditingPlayer(null)}
        />
      )}
      {showCustomPlayerForm && (
        <CustomPlayerForm
          player={editingCustomPlayer}
          onSave={handleSaveCustomPlayer}
          onCancel={() => {
            setShowCustomPlayerForm(false);
            setEditingCustomPlayer(null);
          }}
        />
      )}
      <Footer onShowLegal={setLegalModal} />
      <CookieBanner />
      <LegalModal type={legalModal} onClose={() => setLegalModal(null)} />
    </div>
  );
}

export default App;